import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plane, Hotel, Car, Shield, MapPin, Loader2, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { flight: Plane, hotel: Hotel, cab: Car, insurance: Shield, tour: MapPin };
const inr = (v) => (v || v === 0 ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—');
const time = (t) => (t ? t.slice(11, 16) : '');

function cardLines(c) {
  switch (c.type) {
    case 'flight':
      return [`${c.airline} ${c.flightNumber}${c.leg === 'COMBO' ? ' · Round trip' : c.leg === 'RETURN' ? ' · Return' : ''}`,
        `${c.origin} ${time(c.departureTime)} → ${c.destination} ${time(c.arrivalTime)} · ${c.stops ? `${c.stops} stop` : 'Direct'}${c.altAirport ? ' · Different airport' : ''}`,
        `Bag: cabin ${c.baggage?.cabin || '—'}, check-in ${c.baggage?.checkin || '—'} · ${c.refundable ? 'Refundable' : 'Non-refundable'}`];
    case 'hotel':
      return [c.name, `${c.roomType || 'Room'} · ${c.mealBasis || ''}`, `${c.nights} night(s) · ${c.isRefundable ? 'Free cancellation window' : 'Non-refundable'}`];
    case 'cab':
      return [c.vehicle, `${c.pickup} → ${c.drop}`, `Seats ${c.paxCapacity || '—'} · fare ${inr(c.totalFare)} + tax ${inr(c.totalTax)}`];
    case 'insurance':
      return [`${c.provider} ${c.name}`, c.region, `${c.benefits?.length || 0} listed benefits (provider wording)`];
    default:
      return [c.title, `${c.duration || ''} · ★ ${c.rating || '—'} (${c.reviewCount || 0})`, c.freeCancellation ? 'Free cancellation (Viator)' : 'Viator tour'];
  }
}

export const AssistantResultCard = ({ card }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const Icon = ICONS[card.type] || MapPin;
  const lines = cardLines(card);
  const basis = card.type === 'flight' ? 'per adult' : card.type === 'tour' ? 'from / person' : card.type === 'hotel' ? 'total stay' : 'total';

  const select = async () => {
    if (card.type === 'tour') return window.open(card.url, '_blank', 'noopener');
    if (card.type === 'cab') return navigate('/cabs', { state: { assistantCab: card } });
    if (card.type === 'insurance') return navigate('/insurance', { state: { assistantPlan: card } });
    if (card.type === 'flight') {
      return navigate('/complete-booking', { state: { type: 'flight', selection: {
        priceId: card.priceId, priceIds: [card.priceId], airline: card.airline, flightNumber: card.flightNumber,
        origin: card.origin, destination: card.destination, departureTime: card.departureTime, arrivalTime: card.arrivalTime,
        duration: card.durationMin, stops: card.stops, price: card.price, currency: 'INR', currencySymbol: '₹',
        destinationCountry: card.destinationCountry } } });
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/tripjack/hotels/pricing`, {
        correlation_id: card.correlationId, hotel_id: card.hotelId, checkin_date: card.checkinDate,
        checkout_date: card.checkoutDate, adults: card.adults, children: 0, rooms: card.rooms });
      const opt = data.data?.options?.[0] || {};
      if (!data.success || !data.data?.reviewHash) throw new Error('pricing');
      navigate('/complete-booking', { state: { type: 'hotel', pricingData: data.data, selection: {
        correlationId: card.correlationId, hotelId: card.hotelId, optionId: opt.optionId || card.optionId,
        reviewHash: data.data.reviewHash, name: card.name, city: card.city, price: opt.pricing?.totalPrice || card.price,
        currency: 'INR', roomType: opt.roomInfo?.[0]?.name || card.roomType, mealBasis: opt.mealBasis || card.mealBasis,
        isRefundable: opt.cancellation?.isRefundable ?? card.isRefundable, image: card.image, checkinDate: card.checkinDate,
        checkoutDate: card.checkoutDate, adults: card.adults, rooms: card.rooms } } });
    } catch {
      toast.error('This room is no longer available at that price. Please ask me to search again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 flex gap-3 items-start" data-testid={`assistant-card-${card.id}`}>
      <div className="mt-0.5 rounded-xl bg-[#E6006E]/15 p-2"><Icon className="w-4 h-4 text-[#FF4FA3]" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-white/50">{card.id}</span>
          <p className="text-sm font-semibold text-white truncate">{lines[0]}</p>
        </div>
        {lines.slice(1).map((l, i) => <p key={i} className="text-xs text-white/60 truncate">{l}</p>)}
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-white" data-testid={`assistant-card-price-${card.id}`}>{inr(card.price)}</p>
        <p className="text-[10px] text-white/45">{basis}</p>
        <button onClick={select} disabled={busy} data-testid={`assistant-card-select-${card.id}`}
          className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#E6006E] px-3 py-1 text-xs font-semibold text-white transition-transform hover:scale-105 disabled:opacity-60">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : card.type === 'tour' ? <ExternalLink className="w-3 h-3" /> : null}
          {card.type === 'tour' ? 'View' : card.type === 'insurance' ? 'Details' : 'Select'}
        </button>
      </div>
    </div>
  );
};
